
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import httpClient from "../../store/httpClient";
import { CustomerDetailDto } from "../../dtos/customerDetailDto";
import { CustomerInfoDto } from "@/dtos/customerInfoDto";
import { AddressDto } from "@/dtos/addressDto";
import { AddressType } from "@/dtos/addressType";
import AddressesOverview from "@/components/AddressesOverview.vue";
import AuditTrail from "@/components/AuditTrail.vue";
import CustomerLogs from "@/components/CustomerLogs.vue";
import CreateCreditNoteDialog from "@/components/CreateCreditNoteDialog.vue";
import UserTile from "@/components/UserTile.vue";
import rules from "@/store/rules";

@Component({
	components: {
		AddressesOverview,
		UserTile,
		AuditTrail,
		CustomerLogs,
		CreateCreditNoteDialog,
	},
})
export default class CustomerDashboard extends Vue {
	@Prop()
	private customerId!: string;
	private loading: boolean = false;
	private kundeLöschenDialog: boolean = false;
	private kundendatenVerschiebenDialog: boolean = false;
	private targetCustomerId: number = 0;
	private creditNoteDialogOpen: boolean = false;
	private validCreditLimit: boolean = true;
	private requiredNonNegativeIntegerRule = rules.requiredNonNegativeIntegerRule;
	private customerMwSt: string = "";
	private customerAdditionalOrderEmail: string = "";
	private tabTypes: { key: string; type: AddressType; display: string }[] = [
		{
			key: "customer",
			type: AddressType.CustomerPersonal,
			display: "Kundendaten",
		},
		{
			key: "users",
			type: AddressType.CustomerPersonal, // Is not used
			display: "Benutzer",
		},
		{
			key: "delivery",
			type: AddressType.CustomerDelivery,
			display: "Lieferadresse",
		},
		{
			key: "invoice",
			type: AddressType.CustomerInvoice,
			display: "Rechnungsadresse",
		},
		{
			key: "creditLimit",
			type: AddressType.CustomerPersonal, // Is not used
			display: "Kreditlimit",
		},
		{
			key: "notes",
			type: AddressType.CustomerPersonal, // Is not used
			display: "Infos",
		},
		{
			key: "auditTrail",
			type: AddressType.CustomerPersonal, // Is not used
			display: "Audit Trail",
		},
		{
			key: "logs",
			type: AddressType.CustomerPersonal, // Is not used
			display: "Logs",
		},
	];
	private logsTabVisited: boolean = false;

	private async mounted() {
		this.loading = true;
		await this.loadData();
	}

	private onTabChange(index: number) {
		const tab = this.tabTypes[index];
		if (tab && tab.key === "logs") {
			this.logsTabVisited = true;
		}
	}

	private async loadData() {
		await this.loadCustomerInfo()
			.then(this.loadCustomerDetail)
			.then(() => {
				this.customerMwSt = this.customer.customerMwstNumber
					? this.customer.customerMwstNumber
					: "";
				this.customerAdditionalOrderEmail = this.customer
					.defaultAdditionalOrderConfirmationEmail
					? this.customer.defaultAdditionalOrderConfirmationEmail
					: "";
			})
			.then(() => (this.loading = false));
	}

	private get addressesLoaded() {
		return (
			this.$store.state.detailCustomerInfo &&
			this.$store.state.detailCustomerInfo.customerId == this.customerId &&
			this.$store.state.detailCustomerDetails &&
			this.$store.state.detailCustomerDetails.customerId == this.customerId
		);
	}

	private get customer(): CustomerInfoDto {
		if (
			this.$store.state.detailCustomerInfo &&
			this.$store.state.detailCustomerInfo.customerId == this.customerId
		) {
			return this.$store.state.detailCustomerInfo;
		}
		// Returning empty object as long as correct informatino is not loaded into state.
		return new CustomerInfoDto();
	}

	private get customerDetail(): CustomerDetailDto {
		if (
			this.$store.state.detailCustomerDetails &&
			this.$store.state.detailCustomerDetails.customerId == this.customerId
		) {
			return this.$store.state.detailCustomerDetails;
		}
		// Returning empty object as long as correct informatino is not loaded into state.
		return new CustomerDetailDto();
	}
	private set customerDetail(details: CustomerDetailDto) {
		this.$store.commit("setDetailCustomerDetails", details);
	}

	private get isPrivateCustomer() {
		if (!this.customer) return true;
		return this.customer.companyName === "privat";
	}

	private async loadCustomerInfo() {
		try {
			const response = await httpClient().get(
				`Customer/GetCustomerInfoById?customerId=${this.customerId}`
			);
			this.$store.commit("setDetailCustomerInfo", response.data);
		} catch (err) {
			console.log("Failed getting customer info.");
			throw err;
		}
	}

	private async loadCustomerDetail() {
		try {
			const response = await httpClient().get(
				`Customer/GetCustomerDetail?customerId=${this.customerId}`
			);
			this.$store.commit("setDetailCustomerDetails", response.data);
		} catch (err) {
			console.log("Failed getting customer details.");
			throw err;
		}
	}

	private get tags() {
		return this.$store.state.tags;
	}

	private back() {
		this.$router.go(-1);
	}

	private async saveCustomerInfos() {
		try {
			if (this.loading) return;
			this.loading = true;
			// Other customer info needs to be sent as well because
			// the function saves all this info at once.
			const customerInfo = {
				customerId: this.customer.customerId,
				companyName: this.customer.companyName,
				street: this.customer.street,
				plz: this.customer.plz,
				town: this.customer.town,
				generalEmail: this.customer.eMail,
				generalPhone: this.customer.phone,
				customerMwstNumber: this.customerMwSt.trim(),
				defaultAdditionalOrderConfirmationEmail: this.customerAdditionalOrderEmail.trim(),
			};
			await httpClient().post("Customer/SaveCustomerInfo", customerInfo);
			this.$store.commit("setSnackbarText", "Kundeninformationen erfolgreich gespeichert.");
		} catch (e) {
			console.error("Save customer info failed.");
			this.$store.commit(
				"setSnackbarErrorText",
				"Kundeninformationen konnten nicht gespeichert werden."
			);
		} finally {
			this.loading = false;
		}
	}

	/**
	 * Saves the customer details.
	 */
	private async saveCustomerDetail() {
		try {
			if (this.loading) {
				return;
			}
			this.loading = true;
			this.customerDetail.tags = this.customer.tags;
			const response = await httpClient().post(
				`Customer/SaveCustomerDetail`,
				this.customerDetail
			);
			if (response.data !== "") {
				this.$store.commit("setSnackbarText", "Änderungen erfolgreich gespeichert.");
			}
		} catch (e) {
			console.error("Save customer failed.");
			console.error(e);
			this.$store.commit(
				"setSnackbarErrorText",
				"Änderungen konnten nicht gespeichert werden."
			);
		} finally {
			this.loading = false;
		}
	}

	private async updateCreditLimit() {
		try {
			if (this.loading || !this.validCreditLimit) {
				return;
			}
			this.loading = true;
			await httpClient().post(
				`Customer/UpdateCreditLimit?customerId=${this.customerId}&newLimit=${this.customerDetail.creditLimit}`
			);
			this.$store.commit("setSnackbarText", "Kreditlimit erfolgreich angepasst.");
		} catch {
			console.log("Credit limit adjustment failed.");
			this.$store.commit(
				"setSnackbarErrorText",
				"Kreditlimit konnte nicht angepasst werden."
			);
		} finally {
			this.loading = false;
		}
	}

	private async updatePrepaymentRequired() {
		try {
			if (this.loading) {
				return;
			}
			this.loading = true;
			await httpClient().post(
				`Customer/UpdatePrepaymentRequired?customerId=${this.customerId}&prepaymentRequired=${this.customerDetail.prepaymentRequired}`
			);
			this.$store.commit("setSnackbarText", "Vorkasse-Einstellung erfolgreich angepasst.");
		} catch {
			console.log("Prepayment required adjustment failed.");
			this.$store.commit(
				"setSnackbarErrorText",
				"Vorkasse-Einstellung konnte nicht angepasst werden."
			);
		} finally {
			this.loading = false;
		}
	}

	private async deleteCustomer() {
		try {
			this.loading = true;
			// Check hier, ob Kunde Bestellungen hat oder nicht
			// Dann entsprechend reagieren und Kunde entfernen oder nur markieren
			// Wenn das delete failt, kann belassen werden, dann hat es wohl nicht funktioniert.

			const response = await httpClient().get(
				`Customer/HasCustomerOrders?customerId=${this.customerId}`
			);
			let hasOrders = response.data;
			await httpClient().delete(`Customer/deleteCustomer?customerId=${this.customerId}`);
			if (!hasOrders) {
				this.$store.commit("removeCustomer", this.customerId);
				this.$store.commit("setSnackbarText", "Kunde wurde mit allen Daten gelöscht.");
			} else {
				this.$store.commit("markCustomerInactive", this.customerId);
				this.$store.commit(
					"setSnackbarText",
					"Bestellungen sind vorhanden. Kunde wurde als inaktiv markiert."
				);
			}
			this.kundeLöschenDialog = false;
			this.$router.push({ name: "customer" });
		} catch {
			this.$store.commit(
				"setSnackbarErrorText",
				"Fehler beim Löschen des Kunden. Log prüfen."
			);
		} finally {
			this.loading = false;
		}
	}

	private async moveCustomerData() {
		try {
			this.loading = true;
			await httpClient().post(
				`Customer/moveCustomerData?sourceCustomerId=${this.customerId}&targetCustomerId=${this.targetCustomerId}`
			);
			this.$store.commit("setSnackbarText", "Daten erfolgreich verschoben.");
			this.kundendatenVerschiebenDialog = false;
		} catch {
			this.$store.commit(
				"setSnackbarErrorText",
				"Fehler beim Verschieben der Daten. Log prüfen."
			);
		} finally {
			this.loading = false;
		}
	}

	private addQuickInvoice() {
		this.$router.push({ name: "createOrder", params: { customerId: this.customerId } });
	}
}
